<script setup lang="ts">
import { personsByKey } from '~/queries/composer'
import IconSpin from '~/assets/icon/general/Spin.svg'

const router = useRouter()
const route = useRoute()
let key = ref(route.query.key)
const pageSize = ref(50)
const currentPage = ref(1)

const url = computed(() => `/index-items?key=${key.value}`)
const { data, pending, error, refresh } = await useAsyncData(
  url.value,
  () =>
    execQuery(personsByKey, {
      key: key.value,
      limit: pageSize.value,
      offset: (currentPage.value - 1) * pageSize.value,
    }),
  { watch: [key, pageSize, currentPage] }
)
const count = computed<number>(() => {
  if (pending.value || error.value) return 0
  return data.value.data.personsByKey.total
})
watch(
  () => route.query.key,
  (newValue) => {
    key.value = newValue
    currentPage.value = 1
    refresh()
  }
)
const { t } = useI18n()
function formatPerson(person) {
  if (!person) return null
  let subtitle
  if (person.birthDate) {
    subtitle = `(${new Date(person.birthDate).getFullYear()}`
    if (person.deathDate) {
      subtitle += `-${new Date(person.deathDate).getFullYear()}`
    }
    subtitle += ')'
  }
  let tags = []
  if (person.hasWorkcourse) {
    tags.push(t('labels.workcourse'))
  }

  if (person.hasBiography) {
    tags.push(t('labels.biography'))
  }
  return {
    ...person,
    title: `${person.firstName} ${person.lastName}`,
    subtitle,
    tags,
    type: tags.length === 0 ? 'worklist' : 'biography',
  }
}

const persons = computed(() => {
  if (pending.value || error.value) return []
  return data.value.data.personsByKey
})

const values = computed(() => {
  if (pending.value || error.value) return null
  return persons.value.persons
})
const keys = computed(() => {
  if (pending.value || error.value) return null
  if (!key.value) {
    key.value = persons.value.keys[0]
  }
  return persons.value.keys
})

function getClasses(label) {
  let res =
    'p-2 border-solid border-2 border-gray-300 transition-colors duration-200 ease-in-out hover:!border-primary-400 hover:text-primary-400'
  if (label === key.value) {
    res += ' bg-primary-400'
  }
  return res
}

const handlePagination = (label) => {
  router.push({
    query: { key: label },
  })
}
</script>

<template>
  <div class="w-10/12 mx-auto mt-4">
    <div class="pagination-buttons">
      <button
        :class="getClasses(key)"
        v-for="key in keys"
        :key="key"
        @click="handlePagination(key)"
      >
        {{ key }}
      </button>
    </div>
    <div class="mt-5">
      <IconSpin v-if="pending" class="h-5 w-5 animate-spin" />
      <ListResource
        v-for="(value, i) in values
          ?.sort((a, b) => a.lastName - b.lastName)
          ?.map(formatPerson) ?? []"
        :key="i"
        :resource="value"
        class="ml-4 first:mt-4 md:px-4 md:first:mt-0"
        minified
      />
    </div>
    <div class="mt-10 flex justify-center md:justify-start">
      <DataPagination
        v-model="currentPage"
        :count="count"
        :page-size="pageSize"
      />
    </div>
  </div>
</template>
